import React, { useState, useEffect } from "react";
import API_URL from "../../api/API";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import { GoArrowLeft } from "react-icons/go";
import { useLocation, Link, useNavigate, useParams } from "react-router-dom";
import { CgClose } from "react-icons/cg";
import { HiOutlinePlusSm } from "react-icons/hi";

// Utility to safely join base + relative path
const getImageUrl = (path) => {
  if (!path) return null;
  return `${API_URL.SERVER_PATH.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
};

const AthleteProfileCard = ({ athlete, onSaveSuccess }) => {
  const { staff } = useStaffAuthContext();

  const navigate = useNavigate();

  const [notes, setNotes] = useState("");
  const [injuryNotes, setInjuryNotes] = useState("");
  const [pr100m, setPr100m] = useState("");
  const [pr200m, setPr200m] = useState("");
  const [pr400m, setPr400m] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [activeModal, setActiveModal] = useState(null);
  // values: "general", "injury", null

  useEffect(() => {
    if (athlete?.notes) setNotes(athlete.notes);
    if (athlete?.injury_notes) setInjuryNotes(athlete.injury_notes);
    if (athlete?.pr_100m !== undefined) setPr100m(athlete.pr_100m);
    if (athlete?.pr_200m !== undefined) setPr200m(athlete.pr_200m);
    if (athlete?.pr_400m !== undefined) setPr400m(athlete.pr_400m);
  }, [athlete]);

  const handleSaveNotes = async () => {
    if (!athlete?._id) return;
    setSaving(true);
    try {
      const response = await fetch(API_URL.SAVE_ATHLETE_NOTES, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${staff.token}`,
        },
        body: JSON.stringify({
          athlete_id: athlete._id,
          notes,
          injury_notes: injuryNotes,
          pr_100m: pr100m,
          pr_200m: pr200m,
          pr_400m: pr400m,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setSaved(true);

        if (onSaveSuccess) {
          await onSaveSuccess(); // ✅ wait for parent to fetch updated athlete
          window.location.reload();
        }

        setTimeout(() => setSaved(false), 2000);
      } else {
        console.error(data.error || "Failed to save notes");
      }
    } catch (err) {
      console.error("Error saving notes:", err);
    } finally {
      setSaving(false);
    }
  };

  if (!athlete) return null;

  return (
    <>
      <div className="bg-bgBase">
        <div className="flex max-md:flex-col justify-between md:items-center gap-5 py-2 px-5 md:px-10 w-full max-w-[1515px]">
          <button
            onClick={() => navigate(-1)}
            className="text-slate100 font-inter font-normal text-sm leading-tight flex items-center gap-2 hover:underline max-md:ml-12"
          >
            <GoArrowLeft color="#666666" /> Back to Athletes
          </button>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setActiveModal("general")}
              className="bg-white hover:bg-tblHead border border-tblBorder rounded-10 py-2.5 px-3 text-dark font-inter font-medium text-xs"
            >
              General Notes
            </button>

            <button
              onClick={() => setActiveModal("injury")}
              className="bg-white hover:bg-tblHead border border-tblBorder rounded-10 py-2.5 px-3 text-dark font-inter font-medium text-xs"
            >
              Injury History
            </button>
            <button className="bg-white hover:bg-tblHead border border-tblBorder rounded-10 py-2.5 px-3 capitalize text-dark font-inter font-medium text-xs">
              Comp Schedule
            </button>
            <Link
              to={`/administrator/athlete/edit/${athlete._id}`}
              className="bg-white hover:bg-tblHead border border-tblBorder rounded-10 py-2.5 px-3 capitalize text-dark font-inter font-medium text-xs inline-block"
            >
              Edit Profile
            </Link>
          </div>
        </div>
      </div>

      <div className="bg-white">
        <div className="w-full max-w-screen-2xl pt-6 pb-2 px-5 md:px-8 xl:px-16">
          <div className="w-full flex max-xl:flex-col justify-between gap-5">
            {/* Left: Profile Picture */}
            <div className="flex gap-4">
              {athlete.photo ? (
                <img
                  src={getImageUrl(athlete.photo)}
                  alt="Profile"
                  className="w-20 h-20 object-cover rounded-full"
                />
              ) : (
                <div className="w-20 h-20 bg-gray-200 flex items-center justify-center font-inter text-gray-500 rounded-full text-[10px]">
                  No Photo
                </div>
              )}

              {/* Middle: Athlete Info */}
              <div className="space-y-2">
                <p className="text-dark font-inter font-medium text-xl md:text-2xl capitalize">
                  {athlete.firstname} {athlete.lastname}
                </p>
                <div className="text-slate100 font-inter font-normal text-sm">
                  <p>
                    {athlete.height}" / {athlete.weight} lbs
                  </p>
                  <p>Training Type: {athlete.training_preference}</p>
                </div>
              </div>
            </div>

            {/* Right: Notes & Pace Section */}
            <div className="w-full xl:max-w-lg flex flex-col xl:justify-center space-y-2">
              <div className="flex max-md:flex-col md:items-end gap-4">
                {/* Pace Fields */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block font-inter font-medium text-slate100 text-xs leading-tight mb-1.5">
                      Goal 100m (s):
                    </label>
                    <input
                      type="number"
                      value={pr100m}
                      onChange={(e) => setPr100m(e.target.value)}
                      className="w-full bg-white border border-tblBorder py-1.5 px-3 rounded-lg text-dark font-inter font-medium text-base outline-none placeholder:font-inter placeholder:font-normal"
                      placeholder="100m time"
                    />
                  </div>
                  <div>
                    <label className="block font-inter font-medium text-slate100 text-xs leading-tight mb-1.5">
                      Goal 200m (s):
                    </label>
                    <input
                      type="number"
                      value={pr200m}
                      onChange={(e) => setPr200m(e.target.value)}
                      className="w-full bg-white border border-tblBorder py-1.5 px-3 rounded-lg text-dark font-inter font-medium text-base outline-none placeholder:font-inter placeholder:font-normal"
                      placeholder="200m time"
                    />
                  </div>
                  <div>
                    <label className="block font-inter font-medium text-slate100 text-xs leading-tight mb-1.5">
                      Goal 400m (s):
                    </label>
                    <input
                      type="number"
                      value={pr400m}
                      onChange={(e) => setPr400m(e.target.value)}
                      className="w-full bg-white border border-tblBorder py-1.5 px-3 rounded-lg text-dark font-inter font-medium text-base outline-none placeholder:font-inter placeholder:font-normal"
                      placeholder="400m time"
                    />
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex items-center justify-between">
                  <button
                    onClick={handleSaveNotes}
                    disabled={saving}
                    className="bg-bgBase border border-tblBorder py-2.5 px-11 rounded-lg text-dark whitespace-nowrap font-inter text-xs font-medium hover:bg-tblHead"
                  >
                    {saving ? "Saving..." : "Save Goals"}
                  </button>
                  {saved && (
                    <span className="text-green-600 text-xs">Saved ✓</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {activeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center backdrop-blur-sm z-50 px-5">
          <div className="bg-white rounded-14 w-full max-w-full sm:max-w-[85%] xl:max-w-screen-xl shadow-lg">
            <div className="p-5 xl:p-9 flex justify-between items-center">
              <h3 className="text-dark font-inter font-medium text-xl md:text-2xl">
                {activeModal === "general" ? "General Notes" : "Injury History"}
              </h3>

              <button onClick={() => setActiveModal(null)} className="p-2 rounded-sm shrink-0 hover:bg-tblBorder/50">
                <CgClose color="#666666" size={20} />
              </button>
            </div>

            <div className="p-4 xl:p-8 border-t border-tblBorder">
              {/*<div className="bg-bgBase rounded-10 p-4 xl:p-6 space-y-3 mb-6">
                <p className="text-dark font-inter font-normal text-base">
                  Athlete showed great improvement in sprint times. Recommend
                  focusing on strength training.
                </p>

                <p className="text-slate100 font-inter font-medium text-sm">
                  Coach Mike <span className="mx-2">•</span>{" "}
                  <span className="font-inter font-normal">
                    March 30, 2026 at 2:45 PM
                  </span>
                </p>
              </div>*/}

              <div className="pt-5 border-tblBorder">
                <div>
                  <label
                    htmlFor="note"
                    className="block font-inter text-slate100 text-sm font-medium mb-3"
                  >
                    Add New Note
                  </label>
                  <textarea
                    value={activeModal === "general" ? notes : injuryNotes}
                    onChange={(e) =>
                      activeModal === "general"
                        ? setNotes(e.target.value)
                        : setInjuryNotes(e.target.value)
                    }
                    rows={5}
                    className="w-full border border-tblBorder py-3 px-4 rounded-10 font-inter font-normal text-base outline-none"
                    placeholder={
                      activeModal === "general"
                        ? "Enter general notes..."
                        : "Enter injury notes..."
                    }
                  />
                </div>

                <div className="flex flex-wrap justify-end gap-3 mt-4">
                  <button
                    onClick={() => setActiveModal(null)}
                    className="bg-transparent border border-tblBorder rounded-10 py-2.5 px-5 text-dark font-inter font-medium text-sm leading-tight capitalize hover:bg-dark hover:text-white transition"
                  >
                    Cancel
                  </button>

                  {/* <button
                  onClick={async () => {
                    await handleSaveNotes(); // ✅ SAME FUNCTION
                    setActiveModal(null);
                  }}
                  disabled={saving}
                  className="px-4 py-2 bg-dark text-white text-sm rounded-lg"
                >
                  {saving ? "Saving..." : "Save"}
                </button> */}

                  <button
                    onClick={async () => {
                      await handleSaveNotes(); // ✅ SAME FUNCTION
                      setActiveModal(null);
                    }}
                    disabled={saving}
                    className="px-6 py-2.5 flex items-center gap-2 bg-dark text-white font-inter font-normal text-sm leading-tight capitalize rounded-lg hover:bg-black"
                  >
                    <HiOutlinePlusSm color="#fff" size={16} /> add note
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AthleteProfileCard;
